import React, { useState } from "react";
import { motion } from "framer-motion";
import { Scale, Ruler, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = "/api";

const OnboardingModal = ({ onComplete }) => {
  const [bodyweight, setBodyweight] = useState("");
  const [unit, setUnit] = useState("lbs");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let bw = parseFloat(bodyweight);
    if (!bw || bw <= 0) return toast.error("Please enter your bodyweight");

    // Convert kg to lbs if needed (store as lbs internally)
    if (unit === "kg") bw = Math.round(bw * 2.20462 * 10) / 10;

    const heightInches = heightFt ? (parseInt(heightFt) * 12) + (parseInt(heightIn) || 0) : null;

    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ bodyweight: bw, height_inches: heightInches }),
      });
      if (res.ok) {
        toast.success("Profile saved — let's go!");
        onComplete(bw);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="onboarding-modal">
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", damping: 25 }}
        className="w-full max-w-md mx-4"
      >
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl border border-[#334155] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/20 to-emerald-500/20 px-6 py-6 text-center">
            <h1 className="text-2xl font-bold text-white">Welcome to Paladin</h1>
            <p className="text-sm text-gray-300 mt-1">One quick thing before we start</p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
            {/* Bodyweight (required) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                <Scale className="w-4 h-4 text-primary" /> Bodyweight <span className="text-red-400">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.1"
                  min="50"
                  max="500"
                  value={bodyweight}
                  onChange={(e) => setBodyweight(e.target.value)}
                  placeholder={unit === "lbs" ? "e.g. 185" : "e.g. 84"}
                  className="flex-1 bg-[#0B0F1A] border border-[#334155] rounded-xl px-4 py-3 text-white text-lg font-medium placeholder-gray-600 focus:border-primary focus:outline-none transition-colors"
                  autoFocus
                  data-testid="onboarding-bodyweight"
                />
                <div className="flex rounded-xl overflow-hidden border border-[#334155]">
                  {["lbs", "kg"].map(u => (
                    <button key={u} type="button" onClick={() => setUnit(u)}
                      className={`px-4 py-3 text-sm font-bold transition-colors ${unit === u ? "bg-primary text-white" : "bg-[#0B0F1A] text-gray-400 hover:text-white"}`}
                      data-testid={`unit-${u}`}>
                      {u}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">Used for weight progressions and nutrition calculations</p>
            </div>

            {/* Height (optional) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                <Ruler className="w-4 h-4 text-emerald-400" /> Height <span className="text-gray-500 text-xs font-normal">(optional)</span>
              </label>
              <div className="flex gap-2">
                <div className="flex items-center gap-1 flex-1">
                  <input type="number" min="3" max="8" value={heightFt} onChange={(e) => setHeightFt(e.target.value)}
                    placeholder="5" className="w-full bg-[#0B0F1A] border border-[#334155] rounded-xl px-4 py-3 text-white font-medium placeholder-gray-600 focus:border-primary focus:outline-none" data-testid="onboarding-height-ft" />
                  <span className="text-gray-500 text-sm">ft</span>
                </div>
                <div className="flex items-center gap-1 flex-1">
                  <input type="number" min="0" max="11" value={heightIn} onChange={(e) => setHeightIn(e.target.value)}
                    placeholder="10" className="w-full bg-[#0B0F1A] border border-[#334155] rounded-xl px-4 py-3 text-white font-medium placeholder-gray-600 focus:border-primary focus:outline-none" data-testid="onboarding-height-in" />
                  <span className="text-gray-500 text-sm">in</span>
                </div>
              </div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white py-3.5 rounded-xl text-sm font-bold transition-colors"
              data-testid="onboarding-submit">
              {saving ? "Saving..." : <>Let's Train <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingModal;
